import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { ArrowUp, ArrowDown, Users, CurrencyDollar, Calendar, ChartLineUp } from '@phosphor-icons/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const formatCLP = (amount) => {
  return new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

const DashboardPage = () => {
  const { axiosInstance, isAdmin } = useAuth();
  const [stats, setStats] = useState(null);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const params = selectedBranch ? { branch_id: selectedBranch } : {};
      const [statsRes, branchesRes] = await Promise.all([
        axiosInstance.get('/api/statistics', { params }),
        isAdmin ? axiosInstance.get('/api/branches') : Promise.resolve({ data: [] }),
      ]);
      setStats(statsRes.data);
      setBranches(branchesRes.data);
    } catch (error) {
      // error handled silently
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, isAdmin, selectedBranch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  const kpis = [
    {
      label: 'Total Hoy',
      value: stats?.today?.total || 0,
      count: stats?.today?.count || 0,
      icon: CurrencyDollar,
      color: 'text-zinc-900',
    },
    {
      label: 'Total Semana',
      value: stats?.week?.total || 0,
      count: stats?.week?.count || 0,
      icon: Calendar,
      color: 'text-zinc-900',
    },
    {
      label: 'Total Mes',
      value: stats?.month?.total || 0,
      count: stats?.month?.count || 0,
      icon: ChartLineUp,
      color: 'text-zinc-900',
    },
    {
      label: 'Trabajadores con Anticipos',
      value: stats?.workers_with_advances || 0,
      isCount: true,
      icon: Users,
      color: 'text-zinc-900',
    },
  ];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-zinc-900" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Resumen de anticipos y estadísticas
          </p>
        </div>
        {isAdmin && branches.length > 0 && (
          <select
            value={selectedBranch}
            onChange={(e) => setSelectedBranch(e.target.value)}
            className="form-select w-48"
            data-testid="branch-filter"
          >
            <option value="">Todas las sucursales</option>
            {branches.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {kpis.map((kpi) => (
          <div key={kpi.label} className="kpi-card" data-testid={`kpi-${kpi.label.toLowerCase().replace(/\s+/g, '-')}`}>
            <div className="flex items-start justify-between mb-3">
              <span className="kpi-label">{kpi.label}</span>
              <kpi.icon size={20} className={kpi.color} />
            </div>
            <div className="kpi-value">
              {kpi.isCount ? kpi.value : formatCLP(kpi.value)}
            </div>
            {!kpi.isCount && (
              <p className="text-xs text-zinc-500 mt-1">
                {kpi.count} {kpi.count === 1 ? 'anticipo' : 'anticipos'}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Daily Average */}
      <div className="kpi-card">
        <div className="flex items-center gap-4">
          <div>
            <span className="kpi-label">Promedio Diario del Mes</span>
            <div className="kpi-value mt-1">
              {formatCLP(stats?.daily_average || 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Trend Chart */}
        <div className="chart-container">
          <h3 className="font-heading text-lg font-semibold text-zinc-900 mb-4">
            Tendencia Últimos 30 Días
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats?.daily_trend || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => value.slice(5)}
                  stroke="#a1a1aa"
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  stroke="#a1a1aa"
                />
                <Tooltip
                  formatter={(value) => [formatCLP(value), 'Total']}
                  labelFormatter={(label) => `Fecha: ${label}`}
                  contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0 }}
                />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#18181b"
                  strokeWidth={2}
                  dot={{ fill: '#18181b', r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Branches Chart (Admin only) */}
        {isAdmin && stats?.top_branches?.length > 0 && (
          <div className="chart-container">
            <h3 className="font-heading text-lg font-semibold text-zinc-900 mb-4">
              Anticipos por Sucursal (Mes)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats.top_branches} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    stroke="#a1a1aa"
                  />
                  <YAxis
                    type="category"
                    dataKey="branch_name"
                    tick={{ fontSize: 11 }}
                    width={100}
                    stroke="#a1a1aa"
                  />
                  <Tooltip
                    formatter={(value) => [formatCLP(value), 'Total']}
                    contentStyle={{ border: '1px solid #e4e4e7', borderRadius: 0 }}
                  />
                  <Bar dataKey="total" fill="#18181b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Top Workers Table */}
      {stats?.top_workers?.length > 0 && (
        <div className="table-container">
          <div className="p-4 border-b border-zinc-200">
            <h3 className="font-heading text-lg font-semibold text-zinc-900">
              Ranking Trabajadores con Más Anticipos (Mes)
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="top-workers-table">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50">
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    #
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Código
                  </th>
                  <th className="text-left px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Nombre
                  </th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Total Anticipos
                  </th>
                  <th className="text-right px-4 py-3 text-xs uppercase tracking-wider font-medium text-zinc-500">
                    Cantidad
                  </th>
                </tr>
              </thead>
              <tbody>
                {stats.top_workers.map((worker, index) => (
                  <tr key={worker.worker_id} className="data-row border-b border-zinc-100">
                    <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                      {index + 1}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-zinc-600">
                      {worker.worker_code}
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-900">
                      {worker.worker_name}
                    </td>
                    <td className="px-4 py-3 text-sm font-mono text-right text-zinc-900">
                      {formatCLP(worker.total)}
                    </td>
                    <td className="px-4 py-3 text-sm text-right text-zinc-600">
                      {worker.count}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
